import {type KpiDashboardLocationData} from '@domain/kpis'
import {Flag} from '@hconnect/uikit/src/lib2'
import {H4} from '@hm/fundamental'
import {FC} from 'react'

type CountryHeaderProps = {
  countryData: Pick<KpiDashboardLocationData, 'id' | 'countryCode' | 'name'>
  onClick?: () => void
}

/**
 * Country heading (flag + name) of a KPI dashboard location.
 *
 * MIGRATION NOTE — Kiln and Mill status → Suite (TODO: KILN_MILL_STATUS_SUITE_MIGRATION)
 * Extracted into this shared widget because two places render it:
 *  - Performance Reports feature (`@features/performance-reports`): mobile Actual vs Planned cards
 *    (`components/actualVsPlanned.columns.tsx`).
 *  - HProduce app, Kiln and Mill status pages (`/status`, `KilnMillStatus` and `AreaStatus`) via
 *    `packages/hproduce/src/performanceDashboard/components/CollapseLocationItem.tsx`, which passes an
 *    `onClick` navigating to the country's `/status/location/:id` page only when
 *    `useNavAccessContext().isLocationAllowed` allows it.
 * The Kiln and Mill status pages are the remaining HProduce-only part of the performance dashboard
 * and are planned to move into a Suite feature. When migrating them:
 *  - import this header from `@widgets/country-header` in the new feature (features must not import
 *    another feature's UI, so do not take it from `@features/performance-reports`);
 *  - pass an `onClick` navigating with the feature's Suite routes and access rules instead of the
 *    HProduce ones;
 *  - remove the HProduce consumer once the status pages are served by the feature.
 */
export const CountryHeader: FC<CountryHeaderProps> = ({countryData, onClick}) => {
  const {countryCode, name} = countryData

  return (
    <H4 className={`mb-4 ${onClick ? 'cursor-pointer' : 'cursor-default'}`} onClick={onClick}>
      {countryCode && (
        <Flag
          countryCode={countryCode}
          alt={countryCode}
          sx={{
            verticalAlign: 'text-bottom',
            mr: 1,
            backgroundColor: 'common.white'
          }}
        />
      )}
      {name}
    </H4>
  )
}
